import styled, { css } from 'styled-components';
import { ANIMATION_DURATION, getSpacingToken, getTextTokenPreset, getToken } from '@optimus-web/common';
import { Badge, Button, Icon, IconWrapper, ListItem, ListItemProps, Stack } from '@optimus-web/core';
import { HEIGHT_APP_NAV_BAR } from '../../../components';

interface DrawerContainerProps {
    focused: boolean;
    hasResult: boolean;
    showRecent: boolean;
    isFetched: boolean;
    isFetching?: boolean;
    isMobileOrTablet: boolean;
    chatPanelSearch: boolean;
}

export const DrawerContainer = styled(Stack)<DrawerContainerProps>`
    height: fit-content;
    max-height: 500px;
    overflow: hidden;

    ${(props) =>
        !props.chatPanelSearch &&
        css`
            background: ${getToken('backgroundStaticFlat')};
            border-radius: 0 0 ${getToken('borderRadius200')}px ${getToken('borderRadius200')}px;
            transition: max-height ${ANIMATION_DURATION.fast}ms;
            transition-delay: ${ANIMATION_DURATION.fast}ms;
            box-shadow: ${getToken('shadow100')};
        `};

    top: ${HEIGHT_APP_NAV_BAR};
    gap: 0;

    ${(props) =>
        props.isMobileOrTablet &&
        !props.chatPanelSearch &&
        css`
            margin-left: 40px;
        `}

    ${(props) =>
        !props.focused &&
        !props.chatPanelSearch &&
        css`
            transition-delay: 0ms;
            margin-top: -1px;
            max-height: 0;
        `}

    ${(props) =>
        props.focused &&
        !props.hasResult &&
        !props.showRecent &&
        css`
            border-top: none;
        `}

    ${(props) =>
        props.focused &&
        (props.hasResult || props.showRecent || props.isFetching) &&
        css`
            max-height: 80vh;
        `}

    ${(props) =>
        props.focused &&
        !props.hasResult &&
        !props.showRecent &&
        props.isFetched &&
        css`
            max-height: 80vh;
        `}
`;

export const StyledListItem = styled(ListItem).attrs({ className: 'drawer-list-item' })`
    padding: ${getSpacingToken('50')}px ${getSpacingToken('100')}px;
    border-radius: ${getToken('borderRadius100')}px;
    min-height: max-content;

    &:hover {
        background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
        cursor: pointer;

        ${IconWrapper} {
            color: ${getToken('textStaticTertiary')};
        }
    }

    &:active {
        background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
    }

    ${ListItem.ContentElement} {
        height: 100%;
    }

    ${ListItem.IconElement} {
        margin-right: ${getSpacingToken('100')}px;
        margin-top: ${getSpacingToken('25')}px;
    }

    ${ListItem.LeftContentElement} {
        align-items: flex-start;
        height: 100%;
    }

    ${ListItem.PrimaryTextElement} {
        color: ${getToken('textStaticPrimary')};
        ${getTextTokenPreset('bodyMediumStrong')};
        height: 100%;
    }

    ${ListItem.SecondaryTextElement} {
        color: ${getToken('textStaticPrimary')};
        ${getTextTokenPreset('bodySmall')};
        height: 100%;
    }

    &:focus-within {
        background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
    }

    ${(props) =>
        props.focused &&
        css`
            background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
        `}
`;

export const RemoveSearchPromptButton = styled(Button).attrs({
    className: 'remove-search-prompt-button',
})`
    color: ${getToken('textStaticSecondary')};
    height: 16px;
    background: none;
    border: none;

    &:hover {
        background-color: transparent;
        border: none;
        &:not(:disabled) {
            background-color: transparent;
            border: none;
        }
    }
`;

export const StyledRecentAndFavoriteListItem = styled(StyledListItem).attrs({
    className: 'drawer-list-item',
})<ListItemProps>`
    padding-right: ${getSpacingToken('50')}px;
    align-items: center;

    &:focus {
        ${StyledListItem.RightContentElement} {
            ${RemoveSearchPromptButton} {
                visibility: visible !important;
            }
        }
    }

    &:focus-within {
        background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};

        ${StyledListItem.RightContentElement} {
            ${RemoveSearchPromptButton} {
                visibility: visible !important;
            }
        }
    }

    &:hover {
        ${StyledListItem.RightContentElement} {
            ${RemoveSearchPromptButton} {
                visibility: visible !important;
            }
        }
    }

    ${(props) =>
        props.focused &&
        css`
            background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};

            ${StyledListItem.RightContentElement} {
                ${RemoveSearchPromptButton} {
                    visibility: visible !important;
                }
            }
        `}
`;

export const StyledSmartSearchListItem = styled(StyledListItem)`
    border: 1.5px solid ${getToken('borderStaticSecondary')};
    padding: ${getSpacingToken('100')}px;
`;

export const ShowButton = styled.div`
    color: ${getToken('backgroundAccentPrimary')};
    ${getTextTokenPreset('bodySmall')};
    cursor: pointer;
`;
export const StyledLeftIcon = styled(Icon)`
    color: ${getToken('textDisabled')};
`;

export const DrawerContainerStack = styled(Stack)`
    overflow-y: auto;
    height: 100%;
    padding-top: ${getSpacingToken('100')}px;
    gap: ${getSpacingToken('50')}px;
`;

export const LoadingStack = styled(Stack)`
    padding: ${getSpacingToken('1200')}px;
    height: 100%;
`;

export const ThinDivider = styled.div`
    border-top: ${getToken('borderWidth100')}px solid ${getToken('borderStaticSecondary')};
`;

export const StyledBadge = styled(Badge)`
    align-self: center;
`;

export const AssistantContainerBackdrop = styled.div`
    width: 100%;
    height: 100%;
    position: fixed;
    opacity: 0;
    top: ${HEIGHT_APP_NAV_BAR};
    left: 0;
    z-index: -1;
`;
